import React from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import LobbyPlayerBox from "./LobbyPlayerBox.jsx";
import FriendSlot from "./FriendSlot.jsx";
import { colors } from "../../theme/colors";
import { MOBILE_NAV_MQ } from "./lobbyPageStyles";

const BOUNCE_ANIMATION = {
    "0%, 100%": { transform: "translateX(-50%) translateY(0)" },
    "50%": { transform: "translateX(-50%) translateY(-6px)" },
};

const PARTY_STACK_SX = {
    bottom: 0,
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    gap: "1rem",
    width: "100%",
    maxWidth: "100%",
    alignSelf: "stretch",
    margin: "0 auto",
    paddingTop: "4.6rem",
    paddingBottom: "0.25rem",
    paddingLeft: "0.7rem",
    paddingRight: "0.7rem",
    boxSizing: "border-box",
    pointerEvents: "auto",
};

const PARTY_PLAYER_ROW_SX = {
    position: "relative",
    zIndex: 10,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "1rem",
    pointerEvents: "none",
    marginTop: "4rem",
    paddingTop: "5rem",
    paddingBottom: "0.25rem",
    boxSizing: "border-box",
    [MOBILE_NAV_MQ]: PARTY_STACK_SX,
};

const PARTY_SIDE_SLOT_SX = {
    flexShrink: 0,
    pointerEvents: "auto",
    transform: "none",
    zIndex: 2,
    [MOBILE_NAV_MQ]: {
        width: "100%",
        maxWidth: "100%",
        display: "flex",
        alignItems: "stretch",
        "& > *": {
            width: "100%",
            maxWidth: "100%",
        },
    },
};

const PARTY_IS_YOU_COLUMN_SX = {
    flexShrink: 0,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "auto",
    transform: "none",
    zIndex: 4,
    [MOBILE_NAV_MQ]: {
        width: "100%",
        maxWidth: "100%",
        alignItems: "stretch",
        order: -1,
        "& > *:not(.yourselfIcon)": {
            width: "100%",
            maxWidth: "100%",
        },
    },
};

export default function LobbyPlayerStage({
    actions,
    avatarCacheBust,
    canFlipCards,
    currentLobbyCode,
    friendlist,
    friendsOnlineCount,
    handleCancelInvite,
    handleKickPlayerFromLobby,
    handleSendRequestFriend,
    lobbyState,
    otherMembers,
    pendingBySlot,
    pendingRequests,
    selfMember,
    user,
}) {
    const renderFriendSlot = (index) => {
        const slotPlayer = otherMembers[index];
        return (
            <FriendSlot
                key={slotPlayer?.userId ?? slotPlayer?._id ?? `empty-${index}`}
                player={slotPlayer}
                sx={PARTY_SIDE_SLOT_SX}
                onInvite={() => actions.setFriendSidebarOpen(true)}
                friendsOnline={friendsOnlineCount}
                pendingRequests={pendingRequests}
                pendingInvite={pendingBySlot[index] || null}
                onCancelInvite={handleCancelInvite}
                onKick={handleKickPlayerFromLobby}
                friendlist={friendlist}
                handleSendRequestFriend={handleSendRequestFriend}
                currentLobbyCode={currentLobbyCode}
                lobbyState={lobbyState}
                isCurrentUserLeader={selfMember?.role === "host"}
                canFlipCards={canFlipCards}
                showUnavailableActionMessage={actions.showUnavailableActionMessage}
                avatarCacheBust={avatarCacheBust}
            />
        );
    };

    return (
        <Box sx={PARTY_PLAYER_ROW_SX}>
            {renderFriendSlot(0)}
            {renderFriendSlot(1)}
            <Box className="mainPlayerCard" sx={PARTY_IS_YOU_COLUMN_SX}>
                <ArrowDropDown
                    className="yourselfIcon"
                    sx={{
                        position: "absolute",
                        top: "-2.4rem",
                        left: "50%",
                        transform: "translateX(-50%)",
                        color: colors.primary.main,
                        fontSize: "2.2rem",
                        filter: `drop-shadow(0 2px 4px ${colors.primary.main})`,
                        animation: "bounce 2s ease-in-out infinite",
                        "@keyframes bounce": { ...BOUNCE_ANIMATION },
                        [MOBILE_NAV_MQ]: {
                            display: "none",
                        },
                    }}
                />
                <LobbyPlayerBox
                    player={selfMember || user}
                    isYou={true}
                    lobbyState={lobbyState}
                    currentLobbyCode={currentLobbyCode}
                    canFlipCards={canFlipCards}
                    showUnavailableActionMessage={actions.showUnavailableActionMessage}
                    avatarCacheBust={avatarCacheBust}
                />
            </Box>
            {renderFriendSlot(2)}
            {renderFriendSlot(3)}
        </Box>
    );
}

LobbyPlayerStage.propTypes = {
    actions: PropTypes.object.isRequired,
    avatarCacheBust: PropTypes.number,
    canFlipCards: PropTypes.bool,
    currentLobbyCode: PropTypes.string,
    friendlist: PropTypes.array,
    friendsOnlineCount: PropTypes.number,
    handleCancelInvite: PropTypes.func,
    handleKickPlayerFromLobby: PropTypes.func.isRequired,
    handleSendRequestFriend: PropTypes.func.isRequired,
    lobbyState: PropTypes.string,
    otherMembers: PropTypes.array,
    pendingBySlot: PropTypes.object,
    pendingRequests: PropTypes.array,
    selfMember: PropTypes.object,
    user: PropTypes.object,
};
